import React, { useLayoutEffect, useState } from 'react';
import { differenceWith, find, isEqual } from 'lodash';
import { Waypoint } from 'react-waypoint';

import usePathname from '@magoosh/utils/use_pathname';

import { Activity, Interaction, TutoringSession } from '@test_prep/app/ai_tutor/types';

import InteractionContent from '../interaction_content';

interface Props {
  activity: Activity;
  addOrUpdateUserInteractions: (interactions: Interaction[]) => void;
  setCurrentActivity: (activity: Activity) => void;
  submitUserInput: (text: string) => void;
  tutoringSession: TutoringSession;
}

const InteractionsList: React.FC<Props> = (props) => {
  const pathname = usePathname();
  const [previousInteractions, setPreviousInteractions] = useState<Interaction[]>(props.tutoringSession.interactions);
  const [viewedInteractionIds, setViewedInteractionIds] = useState<number[]>([]);

  const interactionsToRender = props.tutoringSession.interactions.filter((interaction, idx) => {
    const followingInteraction = props.tutoringSession.interactions[idx + 1];

    if (interaction.type === 'context_delta') return false;
    //Don't show past context changes with no interactions
    if (interaction.type === 'context_change' && followingInteraction?.type === 'context_change') {
      return false;
    }
    // Don't show a context change for the previous path there were no interactions
    if (interaction.type === 'context_change' && interaction.content.path !== pathname && !followingInteraction) {
      return false;
    }

    return true;
  });

  // Whenever there are new interactions scroll to make them visible
  useLayoutEffect(() => {
    const newInteractions = differenceWith(
      interactionsToRender,
      previousInteractions,
      isEqual
    );
    setPreviousInteractions(props.tutoringSession.interactions);

    const newInteraction = newInteractions[0];
    if (!newInteraction) return;

    const newInteractionElement = document.getElementById(newInteraction.id);

    // If there is a new context change from navigating to a new page, and the list isn't already
    // scrolled to the very bottom, don't jump to the new interaction.
    if (newInteraction.type === 'context_change') {
      const wrapper = document.getElementById('tutoring-panel-interactions');

      // The new element is already renedered so we need to subtract it out when checking if we
      // were already scrolled to the very bottom. The extra 20 is for the flex gap.
      const newElementHeight = newInteractionElement.offsetHeight + 20;
      if (wrapper.scrollTop !== (wrapper.scrollHeight - newElementHeight - wrapper.offsetHeight)) return;
    }

    // If the user manually scrolled away while the interaction was streaming in, don't jump them
    // back to the interaction.
    if (viewedInteractionIds.indexOf(newInteraction.id) !== -1) return;

    newInteractionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [props.tutoringSession.interactions]);

  const markInteractionAsViewed = (interaction) => {
    setViewedInteractionIds([...viewedInteractionIds, interaction.id]);
  };

  return (
    <div className="d-flex flex-1 flex-column gap-m">
      {
        interactionsToRender.map((interaction) => (
          <div key={interaction.id} id={interaction.id.toString()}>
            <Waypoint
              onLeave={() => markInteractionAsViewed(interaction)}
            />

            <div className="d-flex flex-1 flex-column gap-m">
              <InteractionContent
                addOrUpdateUserInteractions={props.addOrUpdateUserInteractions}
                interaction={{ ...interaction }}
                setCurrentActivity={props.setCurrentActivity}
                submitUserInput={props.submitUserInput}
              />
            </div>
          </div>
        ))
      }
    </div>
  );
};

export default InteractionsList;
